import {
  type FaceScreeningFalsePositive,
  type FaceScreeningReviewSignal,
  type ReportGovernanceMetadata,
  type Report,
  type ReportReviewMetadata,
  type ReportRetentionHold,
  type ReportStatus,
  type ReportDetail,
} from './report';
import { REPORT_REVIEW_STATUSES } from './reportTaxonomy';

const STATUSES: ReadonlyArray<ReportStatus> = ['pending', 'rejected', 'confirmed'];

const REVIEW_STATUSES = REPORT_REVIEW_STATUSES;

const parseTimestampField = (value: unknown, fieldName: string): number => {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }

  throw new Error(`Invalid ${fieldName}: expected finite number in milliseconds`);
};

const parseEnum = <T extends string>(
  value: unknown,
  fieldName: string,
  allowed: ReadonlyArray<T>
): T => {
  if (typeof value === 'string' && allowed.includes(value as T)) {
    return value as T;
  }

  throw new Error(`Unknown ${fieldName}: ${String(value)}`);
};

const parseStringField = (value: unknown, fieldName: string): string => {
  if (typeof value === 'string') {
    return value;
  }

  throw new Error(`Invalid ${fieldName}: expected string`);
};

const parseOptionalStringField = (value: unknown): string | undefined => {
  if (value === undefined || value === null) {
    return undefined;
  }

  return parseStringField(value, 'mediaIdentity');
};

const parseCoordinate = (value: unknown, fieldName: 'lat' | 'lng'): number => {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }

  throw new Error(`Missing or invalid coordinate "${fieldName}"`);
};

const parseDateOnlyField = (value: unknown, fieldName: string): number => {
  if (typeof value !== 'string') {
    throw new Error(`Invalid ${fieldName}: expected date-only string`);
  }

  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (!match) {
    throw new Error(`Invalid ${fieldName}: expected date-only string`);
  }

  const [, year, month, day] = match;
  return Date.UTC(Number(year), Number(month) - 1, Number(day));
};

const parseOptionalTimestampField = (value: unknown, fieldName: string): number | undefined => {
  if (value === undefined) {
    return undefined;
  }

  return parseTimestampField(value, fieldName);
};

const parseRetentionHold = (value: unknown): ReportRetentionHold | undefined => {
  if (value === undefined) {
    return undefined;
  }

  if (!value || typeof value !== 'object') {
    throw new Error('Invalid retentionHold payload');
  }

  const raw = value as Record<string, unknown>;

  if (typeof raw.active !== 'boolean') {
    throw new Error('Invalid retentionHold.active: expected boolean');
  }

  const retentionHold: ReportRetentionHold = {
    active: raw.active,
  };

  if (raw.reason !== undefined) {
    retentionHold.reason = parseStringField(raw.reason, 'retentionHold.reason');
  }

  if (raw.holdUntilMs !== undefined) {
    retentionHold.holdUntilMs = parseTimestampField(raw.holdUntilMs, 'retentionHold.holdUntilMs');
  }

  return retentionHold;
};

const parseReportGovernanceMetadata = (raw: Record<string, unknown>): ReportGovernanceMetadata => {
  const governanceMetadata: ReportGovernanceMetadata = {};

  const confirmationFinalityAtMs = parseOptionalTimestampField(
    raw.confirmationFinalityAtMs,
    'confirmationFinalityAtMs'
  );
  if (confirmationFinalityAtMs !== undefined) {
    governanceMetadata.confirmationFinalityAtMs = confirmationFinalityAtMs;
  }

  const rejectionFinalityAtMs = parseOptionalTimestampField(
    raw.rejectionFinalityAtMs,
    'rejectionFinalityAtMs'
  );
  if (rejectionFinalityAtMs !== undefined) {
    governanceMetadata.rejectionFinalityAtMs = rejectionFinalityAtMs;
  }

  const retentionHold = parseRetentionHold(raw.retentionHold);
  if (retentionHold !== undefined) {
    governanceMetadata.retentionHold = retentionHold;
  }

  return governanceMetadata;
};

const parseReviewStatus = (value: unknown): ReportStatus => {
  const reviewStatus = parseEnum(value, 'reviewStatus', REVIEW_STATUSES);
  return reviewStatus === 'awaitingReview' ? 'pending' : reviewStatus;
};

const parseLabels = (value: unknown): ReportReviewMetadata['labels'] => {
  if (Array.isArray(value) && value.every((item) => typeof item === 'string')) {
    return value as string[];
  }

  throw new Error('Invalid report review metadata labels');
};

const parseFaceScreeningWarningKind = (
  value: unknown
): FaceScreeningReviewSignal['warningKind'] => {
  if (value === 'face_detected' || value === 'screening_failed') {
    return value;
  }

  throw new Error(`Unknown face screening warning kind: ${String(value)}`);
};

const parseFaceScreeningReviewSignals = (value: unknown): FaceScreeningReviewSignal[] => {
  if (value === undefined) {
    return [];
  }

  if (!Array.isArray(value)) {
    throw new Error('Invalid face screening review signals payload');
  }

  return value.map((item) => {
    if (!item || typeof item !== 'object') {
      throw new Error('Invalid face screening review signal payload');
    }

    const raw = item as Record<string, unknown>;

    return {
      kind: raw.kind === 'face-screening-review-signal' ? raw.kind : 'face-screening-review-signal',
      mediaStoragePath: parseStringField(raw.mediaStoragePath, 'mediaStoragePath'),
      mediaIdentity: parseOptionalStringField(raw.mediaIdentity),
      warningKind: parseFaceScreeningWarningKind(raw.warningKind),
      overrideAccepted: Boolean(raw.overrideAccepted),
      overrideAcceptedAtMs: parseTimestampField(raw.overrideAcceptedAtMs, 'overrideAcceptedAtMs'),
      screenedAtMs: parseTimestampField(raw.screenedAtMs, 'screenedAtMs'),
      detectorImplementation: parseStringField(
        raw.detectorImplementation,
        'detectorImplementation'
      ),
      detectorModelVersion:
        raw.detectorModelVersion === undefined
          ? undefined
          : parseStringField(raw.detectorModelVersion, 'detectorModelVersion'),
      reviewerSummary: parseStringField(raw.reviewerSummary, 'reviewerSummary'),
      detectorDetailSummary: parseStringField(raw.detectorDetailSummary, 'detectorDetailSummary'),
    };
  });
};

const parseFaceScreeningFalsePositiveHistory = (value: unknown): FaceScreeningFalsePositive[] => {
  if (value === undefined) {
    return [];
  }

  if (!Array.isArray(value)) {
    throw new Error('Invalid face screening false positive history payload');
  }

  return value.map((item) => {
    if (!item || typeof item !== 'object') {
      throw new Error('Invalid face screening false positive payload');
    }

    const raw = item as Record<string, unknown>;

    return {
      kind:
        raw.kind === 'face-screening-false-positive' ? raw.kind : 'face-screening-false-positive',
      mediaStoragePath: parseStringField(raw.mediaStoragePath, 'mediaStoragePath'),
      mediaIdentity: parseOptionalStringField(raw.mediaIdentity),
      warningKind: parseFaceScreeningWarningKind(raw.warningKind),
      overrideAccepted: Boolean(raw.overrideAccepted),
      overrideAcceptedAtMs: parseTimestampField(raw.overrideAcceptedAtMs, 'overrideAcceptedAtMs'),
      screenedAtMs: parseTimestampField(raw.screenedAtMs, 'screenedAtMs'),
      detectorImplementation: parseStringField(
        raw.detectorImplementation,
        'detectorImplementation'
      ),
      detectorModelVersion:
        raw.detectorModelVersion === undefined
          ? undefined
          : parseStringField(raw.detectorModelVersion, 'detectorModelVersion'),
      reviewerSummary: parseStringField(raw.reviewerSummary, 'reviewerSummary'),
      detectorDetailSummary: parseStringField(raw.detectorDetailSummary, 'detectorDetailSummary'),
      classifiedAtMs: parseTimestampField(raw.classifiedAtMs, 'classifiedAtMs'),
      classifiedByUserId: parseStringField(raw.classifiedByUserId, 'classifiedByUserId'),
    };
  });
};

export const adaptReportProjection = (projection: unknown): Report => {
  if (!projection || typeof projection !== 'object') {
    throw new Error('Invalid report projection payload');
  }

  const raw = projection as Record<string, unknown>;

  return {
    id: typeof raw.id === 'string' ? raw.id : '',
    createdAt: parseTimestampField(raw.createdAtMs, 'createdAtMs'),
    date: parseTimestampField(raw.dateMs, 'dateMs'),
    habitatType: parseStringField(raw.habitatType, 'habitatType'),
    location: [parseCoordinate(raw.lat, 'lat'), parseCoordinate(raw.lng, 'lng')],
    observationType: parseStringField(raw.observationType, 'observationType'),
    otherHabitat: typeof raw.otherHabitat === 'string' ? raw.otherHabitat : undefined,
    photos: Array.isArray(raw.photos)
      ? raw.photos.filter((item): item is string => typeof item === 'string')
      : [],
    species: parseStringField(raw.species, 'species'),
    status: parseEnum(raw.status, 'status', STATUSES),
    userId: typeof raw.userId === 'string' ? raw.userId : '',
  };
};

export const adaptBackofficeReportProjection = (projection: unknown): Report => {
  if (!projection || typeof projection !== 'object') {
    throw new Error('Invalid backoffice report projection payload');
  }

  const raw = projection as Record<string, unknown>;

  return {
    ...parseReportGovernanceMetadata(raw),
    id: typeof raw.id === 'string' ? raw.id : '',
    createdAt: parseTimestampField(raw.createdAtMs, 'createdAtMs'),
    date: parseDateOnlyField(raw.sightingDate, 'sightingDate'),
    habitatType: parseStringField(raw.habitatType, 'habitatType'),
    location: [parseCoordinate(raw.lat, 'lat'), parseCoordinate(raw.lng, 'lng')],
    observationType: parseStringField(raw.observationType, 'observationType'),
    otherHabitat: typeof raw.otherHabitat === 'string' ? raw.otherHabitat : undefined,
    photos: Array.isArray(raw.photos)
      ? raw.photos.filter((item): item is string => typeof item === 'string')
      : [],
    species: parseStringField(raw.species, 'species'),
    status: parseReviewStatus(raw.reviewStatus),
    userId: typeof raw.userId === 'string' ? raw.userId : '',
  };
};

export const adaptReportReviewMetadata = (metadata: unknown): ReportReviewMetadata => {
  if (!metadata || typeof metadata !== 'object') {
    throw new Error('Invalid report review metadata payload');
  }

  const raw = metadata as Record<string, unknown>;

  return {
    kind: raw.kind === 'report-review-metadata' ? raw.kind : 'report-review-metadata',
    labels: raw.labels === undefined ? [] : parseLabels(raw.labels),
  };
};

export const adaptReportDetail = (detail: unknown): ReportDetail => {
  if (!detail || typeof detail !== 'object') {
    throw new Error('Invalid report detail payload');
  }

  const raw = detail as Record<string, unknown>;

  return {
    report: adaptReportProjection(raw.report),
    reviewMetadata: adaptReportReviewMetadata(raw.reviewMetadata),
    faceScreeningReviewSignals: parseFaceScreeningReviewSignals(raw.faceScreeningReviewSignals),
    faceScreeningFalsePositiveHistory: parseFaceScreeningFalsePositiveHistory(
      raw.faceScreeningFalsePositiveHistory
    ),
  };
};

export const adaptBackofficeReportDetail = (detail: unknown): ReportDetail => {
  if (!detail || typeof detail !== 'object') {
    throw new Error('Invalid report detail payload');
  }

  const raw = detail as Record<string, unknown>;

  return {
    report: adaptBackofficeReportProjection(raw.report),
    reviewMetadata: adaptReportReviewMetadata(raw.reviewMetadata),
    faceScreeningReviewSignals: parseFaceScreeningReviewSignals(raw.faceScreeningReviewSignals),
    faceScreeningFalsePositiveHistory: parseFaceScreeningFalsePositiveHistory(
      raw.faceScreeningFalsePositiveHistory
    ),
  };
};
